// A resource document as a branded PDF.
//
// Same page furniture as the Ship Happens guide — brandPdf.js owns the
// geometry and the writer — with CLLXTV's own signal rather than the series
// accent. The whole point of this pipeline is that the branding lives here,
// once: change the cover treatment in this file and all forty documents in
// the library change with it, which is not true of forty separate Canva
// exports.
import {
  INK,
  PAPER,
  DIM,
  PAGE,
  MARGIN,
  WIDTH,
  THEMES,
  Writer,
  newDoc,
} from "./brandPdf.js";

const ACCENT = THEMES.cllxtv.accent;

/** Tier order, for picking the lowest bar across a set of documents. */
const RANK = ["member", "leader", "director", "exec", "senior"];

/** The running footer, on every page but the cover. */
function footers(doc, meta) {
  const n = doc.getNumberOfPages();
  for (let p = 2; p <= n; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...DIM);
    doc.text(`${meta.title} · CLLXTV`, MARGIN, PAGE.h - 28);
    doc.text(`${p - 1}`, PAGE.w - MARGIN, PAGE.h - 28, { align: "right" });
  }
}

/** The ink cover a single document opens on. */
function coverPage(doc, meta) {
  doc.setFillColor(...INK);
  doc.rect(0, 0, PAGE.w, PAGE.h, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...ACCENT);
  doc.text("THE ROOM · CLLXTV", MARGIN, 110, { charSpace: 1.8 });

  doc.setFontSize(42);
  doc.setTextColor(...PAPER);
  const titleLines = doc.splitTextToSize(meta.title.toUpperCase(), WIDTH);
  doc.text(titleLines, MARGIN, 170);

  const afterTitle = 170 + titleLines.length * 44;
  doc.setFillColor(...ACCENT);
  doc.rect(MARGIN, afterTitle - 6, 64, 6, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.setTextColor(...PAPER);
  doc.text(doc.splitTextToSize(meta.summary, 400), MARGIN, afterTitle + 34);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...DIM);
  const facts = [
    meta.category,
    `${meta.minutes} MIN READ`,
    `FOR ${String(meta.minLevel).toUpperCase()} AND ABOVE`,
  ].join("   ·   ");
  doc.text(facts.toUpperCase(), MARGIN, PAGE.h - MARGIN - 18, { charSpace: 1.2 });

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...DIM);
  const saved = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(
    `${meta.author} · ${meta.updated || saved}   ·   Saved ${saved}`,
    MARGIN,
    PAGE.h - MARGIN,
  );
}

/**
 * Pours one document's blocks onto the page the writer is currently on.
 *
 * Separate from buildDoc because the bound library renders the same bodies
 * back to back. Two loops would be two documents that slowly stopped looking
 * alike.
 */
function bodyInto(w, blocks) {
  const doc = w.doc;
  for (const b of blocks) {
    if (b.type === "h") {
      w.gap(10);
      w.heading(b.text, 17);
      // The rule under a heading is the document's only repeated accent.
      w.rule(ACCENT);
      w.gap(2);
    } else if (b.type === "p") {
      w.body(b.text, { size: 11, after: 10 });
    } else if (b.type === "ul") {
      for (const item of b.items) {
        // The bullet is drawn rather than typed so it cannot be swallowed by
        // the line wrap, and the text is indented to hang off it.
        w.ensure(20);
        doc.setFillColor(...INK);
        doc.rect(MARGIN + 2, w.y + 5, 3, 3, "F");
        w.body(item, { size: 11, x: MARGIN + 16, width: WIDTH - 16, after: 6 });
      }
      w.gap(4);
    } else if (b.type === "rule") {
      w.gap(8);
      w.rule();
      w.gap(4);
    }
  }
}

/**
 * One document, cover and all.
 *
 * @param doc_  a document from /api/docs/:id — card fields plus `blocks`
 */
export async function buildDoc(doc_) {
  const doc = await newDoc();
  const w = new Writer(doc);
  coverPage(doc, doc_);
  w.page();
  bodyInto(w, doc_.blocks);
  footers(doc, doc_);
  return doc;
}

/**
 * Every document as one bound volume.
 *
 * This is the thing a Drive folder cannot be: a single file to hand somebody,
 * print, or put on a table at a leaders' meeting. Each document keeps its own
 * ink cover so the volume reads as a collection rather than a merge, and the
 * contents page is generated after the fact, once the page numbers are known.
 */
export async function buildLibrary(docs, opts = {}) {
  const title = opts.title || "THE LIBRARY";
  const subtitle =
    opts.subtitle || "Leadership resources, written here, for this house.";

  const doc = await newDoc();
  const w = new Writer(doc);

  // ---- volume cover ----
  coverPage(doc, {
    title,
    summary: subtitle,
    category: `${docs.length} document${docs.length === 1 ? "" : "s"}`,
    minutes: docs.reduce((n, d) => n + d.minutes, 0),
    minLevel: docs.reduce(
      (lowest, d) => (RANK.indexOf(d.minLevel) < RANK.indexOf(lowest) ? d.minLevel : lowest),
      "senior",
    ),
    author: "CLLXTV",
    updated: "",
  });

  // The contents page is reserved now and drawn at the end, because the page
  // numbers it lists do not exist until everything after it has been laid out.
  doc.addPage();
  const contentsPage = doc.getNumberOfPages();

  const entries = [];
  for (const d of docs) {
    doc.addPage();
    coverPage(doc, d);
    doc.addPage();
    w.y = MARGIN;
    entries.push({ title: d.title, category: d.category, page: doc.getNumberOfPages() });
    bodyInto(w, d.blocks);
  }

  // ---- contents, drawn into the page held open for it ----
  doc.setPage(contentsPage);
  const c = new Writer(doc);
  c.y = MARGIN + 10;
  c.heading("Contents", 34);
  c.rule(ACCENT);
  c.gap(14);
  // Grouped, because a flat list repeats the category on every line and still
  // does not show that the volume has a shape.
  let heading = null;
  for (const e of entries) {
    if (e.category !== heading) {
      heading = e.category;
      c.ensure(40);
      c.gap(8);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...DIM);
      doc.text(heading.toUpperCase(), MARGIN, c.y + 8, { charSpace: 1.6 });
      c.y += 18;
    }
    c.ensure(24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text(e.title, MARGIN, c.y + 10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...DIM);
    // Page numbers run from the volume cover, so they match what a reader
    // sees in a PDF viewer rather than the footer's per-document count.
    doc.text(String(e.page), MARGIN + WIDTH, c.y + 10, { align: "right" });
    c.y += 26;
  }

  footers(doc, { title });
  return doc;
}

export { deliver } from "./brandPdf.js";
